import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNotifications } from "@its-engineering-and-development/ui-foundation/providers";
import { REPORT_CATEGORY } from "@app/constants/report-categories";
import type { CategoriesLoadStatus, Category } from "@app/types/categories";
import { CATEGORIES_LOAD_STATUS, CATEGORY_STATUS } from "@app/types/categories";
import { ExpenseCategories, defaultFormFields, resolveExpenseCategoryId } from "@app/constants/expense-categories";
import { TAB, SUB_TAB } from "@app/constants/tabs";
import type { ActiveTab, ActiveSubTab } from "@app/constants/tabs";
import { ExpenseAction, ItemStatusValue, ReportActionValue } from "@app/types/accounting";
import type {
    ExpenseItem,
    ExpenseReport,
    FullExpenseDetail,
    RawFullExpenseDetailResponse,
    ItemStatus,
    ItemsSummary,
    PolicyViolation,
    ReportExpensesData,
    ReportExpenseItemNetwork,
    ReportsSummary,
    ReportStatus,
} from "@app/types/accounting";
import { useStore } from "react-redux";
import { useAppDispatch, useAppSelector, type RootState } from "@app/store";
import { selectAccessToken } from "@app/store/auth.slice";
import { executeGraphqlRequest } from "@app/store/graphql-base-query";
import { isIsoDateKey } from "@app/utils/date";

const GRAPHQL_ENDPOINT = import.meta.env.VITE_ACCOUNTING_GRAPHQL_URL ?? "http://localhost:4001/graphql";
const APPSYNC_ENDPOINT = import.meta.env.VITE_GRAPHQL_ENDPOINT ?? "";
const APPSYNC_API_KEY = import.meta.env.VITE_GRAPHQL_API_KEY ?? "";

const APPROVER_REPORTS_VIEW = "accountant" as const;

const VIOLATION_FIELDS = "violationId policyName policyType status actions { type } justification";

const ITEM_FIELDS = `
    id reportId submitterUserId status merchant category subCategoryId hasReceipt receiptMissing
  settleDate chargeDate exception isPersonal lastFour amount
  accountCode paymentMethod description chargeAmount cardCurrencyAmount
  violations { violationId policyName policyType status actions { type } justification }
  lostReceiptDeclaration { declared initials explanation declaredAt }
  submitter { firstName lastName email }
`;

const ALL_CATEGORIES_QUERY = `
  query AllCategories($limit: Int, $cursor: String) {
    approvalCategories(limit: $limit, cursor: $cursor, filter: { includeDeleted: true, sortBy: categoryName, sortOrder: asc }) {
      items {
        categoryId
        categoryName
        categoryGroup
        status
        accountCode
        legacyId
      }
      pageInfo {
        cursor
        hasNextPage
      }
    }
  }
`;

const ALL_CATEGORIES_PAGE_LIMIT = 50;

const FROZEN_CATEGORY_SELECTION = "frozenCategory { accountCode categoryId categoryName legacyId }";

type AllCategoriesResponse = {
    approvalCategories: { items: Category[]; pageInfo: { cursor: string | null; hasNextPage: boolean } };
};

export interface UpdateExpenseCategoryResult {
    success: boolean;
    message?: string | null;
    // Current violations returned by the update; undefined when the save failed.
    violations?: PolicyViolation[];
}

type CategoryStatePatch = {
    category: string;
    subCategoryId: number | null;
    accountCode: string | null;
    // Present only when the update response returned the expense's current violations.
    violations?: PolicyViolation[];
};

function buildCategoryStatePatch(category: Category): CategoryStatePatch {
    return {
        category: category.categoryId,
        subCategoryId: category.legacyId ?? null,
        accountCode: category.accountCode,
    };
}

function patchExpenseItemCategory<
    T extends { id: string; category: string; subCategoryId?: number | null; accountCode?: string | null; violations?: PolicyViolation[] },
>(items: T[], itemId: string, patch: CategoryStatePatch): T[] {
    return items.map((item) =>
        item.id === itemId
            ? {
                  ...item,
                  category: patch.category,
                  subCategoryId: patch.subCategoryId,
                  accountCode: patch.accountCode ?? item.accountCode,
                  ...(patch.violations ? { violations: patch.violations } : {}),
              }
            : item,
    );
}

function patchReportExpenseCategory<T extends { id: string; category: string; violations?: PolicyViolation[] }>(
    items: T[],
    itemId: string,
    patch: CategoryStatePatch,
): T[] {
    return items.map((item) =>
        item.id === itemId ? { ...item, category: patch.category, ...(patch.violations ? { violations: patch.violations } : {}) } : item,
    );
}

const APPROVER_REPORTS_QUERY = `
  query ApproverReports($limit: Int, $cursor: String, $filter: ApproverReportsFilter) {
    approverReports(limit: $limit, cursor: $cursor, filter: $filter) {
      reports {
                id submitterUserId reportNumber reportName state isEditable canViewExpenses itemCount totalAmount currencyCode submittedAt
        submitter { firstName lastName email }
        expenseStates { expenseId state }
      }
      summary {
                currencyCode
      }
      pageInfo {
        totalCount cursor limit hasNextPage
      }
    }
  }
`;

const EXPORT_APPROVER_REPORTS_QUERY = `
  query ExportApproverReports($filter: ExportApproverReportsFilter) {
    exportApproverReports(filter: $filter)
  }
`;

const REVIEW_ITEMS_QUERY = `query ReviewItems { reviewItems { items { ${ITEM_FIELDS} } currencyCode } }`;
const COMPLETED_ITEMS_QUERY = `query CompletedItems { completedItems { items { ${ITEM_FIELDS} } currencyCode } }`;
const APPROVAL_UI_CONFIG_QUERY = `query ApprovalUiConfig { approvalUiConfig { allowItemsPreview } }`;
const LOCATION_PREDICTIONS_QUERY = `
    query LocationPredictions($input: String!) {
        locationPredictions(input: $input) {
            predictions {
                description
                placeId
            }
        }
    }
`;
const REPORT_EXPENSES_QUERY = `
  query ReportExpenses($reportId: ID!) {
    reportExpenses(reportId: $reportId) {
      items { id reportId category ${FROZEN_CATEGORY_SELECTION} subCategoryId merchant hasReceipt receiptMissing settleDate paymentMethod { method cardKey display { brand last4 description } } amount currencyCode violations { violationId policyName policyType status actions { type } justification } generatedBy lostReceiptDeclaration { declared initials explanation declaredAt } }
      summary { totalCount totalAmount currencyCode }
    }
  }
`;
const GET_REPORT_FULL_EXPENSES_QUERY = `
  query GetReportFullExpenses($reportId: ID!, $expenseId: ID, $submitterName: String) {
    getReportFullExpenses(reportId: $reportId, expenseId: $expenseId, submitterName: $submitterName) {
      reportNumber
      submittedBy
            submittedAt
      items {
        id reportId isEditable merchant category ${FROZEN_CATEGORY_SELECTION} subCategoryId hasReceipt receiptMissing
                settleDate chargeDate expenseDate exception isPersonal reimbursable hasBillable lastFour amount currencyCode
        violations { violationId policyName policyType status actions { type } justification }
        # TODO: restore "status" once getReportFullExpenses returns the correct per-expense
        # status from the server. Currently status is derived from expenseStates on the parent
        # approverReports report, which is the authoritative source until the GraphQL layer is fixed.
        description chargeAmount cardCurrencyAmount cardCurrencyCode
        location participants
        locationFrom locationTo
        client departureDate ticketNumber checkInDate pickUpDate pickupAddress dropoffAddress
        milageLocationFrom
        milageLocationTo
        distance rate distanceRate invoiceNumber generatedBy
        submitter { firstName lastName email }
        paymentMethod { method cardKey display { brand last4 description } }
        lostReceiptDeclaration { declared initials explanation declaredAt }
        receipts {
          receiptId fileType s3URL receiptSource
          mappedFields { merchant expenseDate amount currencyCode }
        }
      }
    }
  }
`;
const UPDATE_EXPENSE_STATUS_MUTATION = `
  mutation UpdateExpenseStatus($reportId: ID!, $expenseIds: [ID!]!, $action: ExpenseAction!) {
    updateExpenseStatus(reportId: $reportId, expenseIds: $expenseIds, action: $action) {
      success
      reportId
      action
      reportStatus
      items {
        expenseId
        oldStatus
        newStatus
      }
      message
    }
  }
`;
const UPDATE_EXPENSE_MUTATION = `
    mutation UpdateExpense($reportId: ID!, $expenseId: ID!, $submitterUserId: ID!, $expense: UpdateExpenseInput!) {
        updateExpense(reportId: $reportId, expenseId: $expenseId, submitterUserId: $submitterUserId, expense: $expense) {
            success
            reportId
            expenseId
            message
            violations { ${VIOLATION_FIELDS} }
        }
    }
`;
const APPROVE_ITEMS_MUTATION = `mutation ApproveItems($itemIds: [ID!]!) { approveItems(itemIds: $itemIds) { ${ITEM_FIELDS} } }`;
const DENY_ITEMS_MUTATION = `mutation SendBackItems($itemIds: [ID!]!) { sendBackItems(itemIds: $itemIds) { ${ITEM_FIELDS} } }`;
const UPDATE_REPORT_MUTATION = `
  mutation UpdateReport($reportId: ID!, $action: ReportAction!) {
    updateReport(reportId: $reportId, action: $action) {
      reportId
      state {
        current
      }
      metadata {
        version
        updatedAt
      }
      reportDetails {
        reportName
        reportNumber
        submitterFirstName
        submitterLastName
        totalAmount
        currency
      }
    }
  }
`;

const GET_REPORT_COMMENTS_QUERY = `
    query GetReportComments($reportId: ID!, $limit: Int, $sort: String, $cursor: String) {
        getReportComments(reportId: $reportId, limit: $limit, sort: $sort, cursor: $cursor) {
            items { commentId authorId authorFullName authorRoles text deleted createdAt updatedAt }
            cursor
            hasMore
        }
    }
`;

const CREATE_REPORT_COMMENT_MUTATION = `
    mutation CreateReportComment($reportId: ID!, $input: CreateCommentInput!) {
        createReportComment(reportId: $reportId, input: $input) { commentId authorId authorRoles text deleted createdAt updatedAt }
    }
`;

const GET_EXPENSE_COMMENTS_QUERY = `
    query GetExpenseComments($expenseId: ID!, $limit: Int, $sort: String, $cursor: String) {
        getExpenseComments(expenseId: $expenseId, limit: $limit, sort: $sort, cursor: $cursor) {
            items { commentId authorId authorFullName authorRoles text deleted createdAt updatedAt }
            cursor
            hasMore
        }
    }
`;

const GET_POLICY_VIOLATION_COMMENTS_QUERY = `
    query GetPolicyViolationComments($violationId: ID!, $limit: Int, $sort: String, $cursor: String) {
        getPolicyViolationComments(violationId: $violationId, limit: $limit, sort: $sort, cursor: $cursor) {
            items { commentId authorId authorFullName authorRoles text deleted createdAt updatedAt }
            cursor
            hasMore
        }
    }
`;

const CREATE_EXPENSE_COMMENT_MUTATION = `
    mutation CreateExpenseComment($expenseId: ID!, $input: CreateCommentInput!) {
        createExpenseComment(expenseId: $expenseId, input: $input) { commentId authorId authorRoles text deleted createdAt updatedAt }
    }
`;

const GET_EXPENSE_HISTORY_QUERY = `
    query GetExpenseHistory($expenseId: ID!, $from: AWSDateTime, $to: AWSDateTime) {
        getExpenseHistory(expenseId: $expenseId, from: $from, to: $to) { items { id event eventDisplay occurredAt actor { type userId displayName } } }
    }
`;

const UPDATE_EXPENSE_INPUT_KEY_MAP: Record<string, string> = {
    amount: "amount",
    currencyCode: "currency",
    chargeDate: "chargeDate",
    merchant: "merchant",
    description: "purpose",
    departureDate: "departureDate",
    ticketNumber: "ticketNumber",
    participants: "participants",
    client: "client",
    checkInDate: "checkInDate",
    pickUpDate: "pickUpDate",
    pickupAddress: "pickupAddress",
    dropoffAddress: "dropoffAddress",
    location: "location",
    locationFrom: "locationFrom",
    locationTo: "locationTo",
    milageLocationFrom: "milageLocationFrom",
    milageLocationTo: "milageLocationTo",
    distance: "distance",
    rate: "rate",
    distanceRate: "distanceRate",
    invoiceNumber: "invoiceNumber",
};

function buildUpdateExpenseInputFromSelectedCategory(
    item: FullExpenseDetail,
    selectedCategory: Category,
    expenseCategoryId: number,
    fieldOverrides: Record<string, unknown> = {},
): Record<string, unknown> {
    const payload: Record<string, unknown> = {
        expenseCategoryId,
        expenseSubCategoryId: selectedCategory.categoryId,
    };

    const legacySubCategoryId = selectedCategory.legacyId;
    const selectedSubCategory =
        legacySubCategoryId != null
            ? ExpenseCategories.flatMap((category) => category.subCategories).find((subCategory) => subCategory.subCategoryId === legacySubCategoryId)
            : undefined;

    const formFields = selectedSubCategory?.formFields ?? defaultFormFields;

    // Include all category-visible fields that have API mappings and are supported by UpdateExpenseInput.
    for (const field of formFields) {
        if (!field.apiKey) continue;

        const updateKey = UPDATE_EXPENSE_INPUT_KEY_MAP[field.apiKey];
        if (!updateKey) continue;

        const rawValue = Object.prototype.hasOwnProperty.call(fieldOverrides, field.apiKey)
            ? fieldOverrides[field.apiKey]
            : (item as unknown as Record<string, unknown>)[field.apiKey];
        if (rawValue == null) continue;

        if (updateKey === "amount") {
            payload.amount = String(rawValue);
            continue;
        }

        if (updateKey === "invoiceNumber") {
            const asNumber = Number(rawValue);
            if (Number.isFinite(asNumber)) payload.invoiceNumber = asNumber;
            continue;
        }

        if (updateKey === "participants") {
            if (Array.isArray(rawValue)) {
                payload.participants = rawValue.map(String).filter((v) => v.length > 0);
            }
            continue;
        }

        payload[updateKey] = rawValue;
    }

    // Payment is displayed in the drawer but has no direct apiKey in category config.
    if (item.paymentMethod?.method) payload.paymentMethod = item.paymentMethod.method;
    if (item.paymentMethod?.display?.description) payload.paymentDescription = item.paymentMethod.display.description;

    // Keep common canonical values aligned with the current expense snapshot.
    if (payload.currency == null && item.currencyCode) payload.currency = item.currencyCode;
    if (item.expenseDate) payload.expenseDate = item.expenseDate;
    if (item.reimbursable != null) payload.reimbursable = item.reimbursable;
    if (item.hasBillable != null) payload.hasBillable = item.hasBillable;
    // Trip service full-overwrites on update, so the AI indicator must be resent or it's lost.
    if (item.generatedBy) payload.generatedBy = item.generatedBy;
    // Same full-overwrite behavior: omitting the declaration would erase it from the expense.
    if (item.lostReceiptDeclaration) {
        const { declared, initials, explanation, declaredAt } = item.lostReceiptDeclaration;
        payload.lostReceiptDeclaration = { declared, initials, explanation, declaredAt };
    }
    return payload;
}

export type ReportSortBy = "submitterName" | "status" | "reportNumber" | "itemCount" | "totalAmount";

export interface ApproverReportsColumnFilter {
    submitterFullNameSearch?: string;
    state?: ReportStatus;
    reportNumber?: string;
    expensesCountMin?: number;
    expensesCountMax?: number;
    totalAmountMin?: number;
    totalAmountMax?: number;
}

/** Raw pageInfo shape returned by the new cursor-based approverReports API. */
interface ApproverReportsRawPageInfo {
    totalCount: number;
    cursor: string | null;
    limit: number;
    hasNextPage: boolean;
}

/**
 * Date range (`updatedDateFrom`/`updatedDateTo`) used to filter reports by their last-updated date.
 * Currently wired up for the Completed reports tab only; kept generic (like `ReportSortBy` and
 * `ApproverReportsColumnFilter`) so the Preview/Review tabs can reuse the same filter shape later.
 */
export interface ReportDateRange {
    updatedDateFrom: string;
    updatedDateTo: string;
}

const REPORT_DATE_RANGE_DEFAULT_DAYS = 30;

function smokeCheckReportDateRange(range: ReportDateRange, source: string): void {
    if (!import.meta.env.DEV) return;
    if (isIsoDateKey(range.updatedDateFrom) && isIsoDateKey(range.updatedDateTo)) return;

    console.warn("[smoke-check] Non-ISO report date range detected", {
        source,
        updatedDateFrom: range.updatedDateFrom,
        updatedDateTo: range.updatedDateTo,
    });
}

function toIsoDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

/** Builds the default report date range: the last `days` days, ending today. */
function getDefaultReportDateRange(days: number = REPORT_DATE_RANGE_DEFAULT_DAYS): ReportDateRange {
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - (days - 1));
    return { updatedDateFrom: toIsoDateKey(from), updatedDateTo: toIsoDateKey(today) };
}

/**
 * Decodes a base64 string into raw bytes.
 *
 * Decodes straight to a `Uint8Array` rather than a JS string so multi-byte UTF-8
 * characters (e.g. accented names) survive intact — passing an `atob()` "binary
 * string" straight into `new Blob([...])` would cause the browser to re-encode
 * it as UTF-8 and corrupt those characters.
 */
function decodeBase64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
}

/** Triggers a browser download of CSV byte content without navigating away from the page. */
function downloadCsvFile(bytes: Uint8Array<ArrayBuffer>, filename: string): void {
    const blob = new Blob([bytes], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/** Synthetic pageInfo shape exposed to the UI — compatible with TablePageInfo from ui-foundation. */
export interface PageInfo {
    totalCount: number;
    offset: number;
    limit: number;
    hasNextPage: boolean;
}

export type { ActiveTab, ActiveSubTab } from "@app/constants/tabs";
export interface SubTabOption {
    key: ActiveSubTab;
    label: string;
    isDisabled: boolean;
}

function withReportPermissions(summary: ReportsSummary, subTab: ActiveSubTab): ReportsSummary {
    const reports = summary.reports.map((report) => {
        if (subTab === SUB_TAB.Preview) {
            return {
                ...report,
                isEditable: false,
                canViewExpenses: false,
            };
        }

        if (subTab === SUB_TAB.Review) {
            return {
                ...report,
                isEditable: true,
                canViewExpenses: true,
            };
        }

        return {
            ...report,
            isEditable: false,
            canViewExpenses: true,
        };
    });

    return {
        ...summary,
        reports,
    };
}

function withItemPermissions(summary: ItemsSummary, subTab: Exclude<ActiveSubTab, typeof SUB_TAB.Preview>): ItemsSummary {
    const items = summary.items.map((item) => ({
        ...item,
        isEditable: subTab === SUB_TAB.Review,
    }));

    return {
        ...summary,
        items,
    };
}

export function useAccountingApproval() {
    const dispatch = useAppDispatch();
    const { showSuccess } = useNotifications();
    const [activeTab, setActiveTabState] = useState<ActiveTab>(TAB.Reports);
    const [activeSubTab, setActiveSubTabState] = useState<ActiveSubTab>(SUB_TAB.Review);

    const [reviewReports, setReviewReports] = useState<ReportsSummary | null>(null);
    const [previewReports, setPreviewReports] = useState<ReportsSummary | null>(null);
    const [completedReports, setCompletedReports] = useState<ReportsSummary | null>(null);
    const [reviewItems, setReviewItems] = useState<ItemsSummary | null>(null);
    const [completedItems, setCompletedItems] = useState<ItemsSummary | null>(null);

    const [reportExpensesMap, setReportExpensesMap] = useState<Record<string, ReportExpensesData>>({});
    // Tracks reportIds whose expenses have already been fetched (persists across renders via ref)
    const fetchedReportExpensesRef = useRef<Set<string>>(new Set());
    // Increments whenever reports sub-tab context changes to invalidate stale async responses.
    const reportExpensesGenerationRef = useRef(0);

    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
    const [selectedItem, setSelectedItem] = useState<ExpenseItem | null>(null);
    const [fullExpenseDetail, setFullExpenseDetail] = useState<FullExpenseDetail | null>(null);
    const fullExpenseDetailIdRef = useRef<string | null>(null);
    // Violations returned by the latest save; they outrank a detail refetch that may carry older data.
    const savedViolationsRef = useRef<{ itemId: string; violations: PolicyViolation[] } | null>(null);
    const [isLoadingReceipt, setIsLoadingReceipt] = useState(false);

    useEffect(() => {
        fullExpenseDetailIdRef.current = fullExpenseDetail?.id ?? null;
    }, [fullExpenseDetail]);

    const [currentPage, setCurrentPage] = useState(0);
    // Cursor history for cursor-based pagination: index = page number, value = cursor to request that page.
    // Page 0 always uses null (no cursor). Stored as a ref to avoid adding it to useCallback deps.
    const cursorHistoryRef = useRef<(string | null)[]>([null]);
    const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);

    const [reportSort, setReportSortState] = useState<{ sortBy: ReportSortBy; sortOrder: "asc" | "desc" } | null>(null);
    // Stable ref so load callbacks can read the latest sort without being re-created on every change
    const reportSortRef = useRef(reportSort);
    reportSortRef.current = reportSort;

    const [reportColumnFilter, setReportColumnFilterState] = useState<ApproverReportsColumnFilter>({});
    const reportColumnFilterRef = useRef(reportColumnFilter);
    reportColumnFilterRef.current = reportColumnFilter;

    // Date range filter, currently applied to the Completed reports tab only. Defaults to the last 30 days.
    const [reportDateRange, setReportDateRangeState] = useState<ReportDateRange>(() => getDefaultReportDateRange());
    const reportDateRangeRef = useRef(reportDateRange);
    reportDateRangeRef.current = reportDateRange;
    const [isExporting, setIsExporting] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [hasLoadedReports, setHasLoadedReports] = useState(false);
    const [isMutating, setIsMutating] = useState(false);
    const [isProcessingReport, setIsProcessingReport] = useState(false);
    const [processingItemId, setProcessingItemId] = useState<string | null>(null);
    const [processingAction, setProcessingAction] = useState<ExpenseAction | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [allowItemsPreview, setAllowItemsPreview] = useState(false);
    const [allCategories, setAllCategories] = useState<Category[]>([]);
    const [categoriesStatus, setCategoriesStatus] = useState<CategoriesLoadStatus>(CATEGORIES_LOAD_STATUS.Loading);
    // Active-only subset for editable selectors; the complete list keeps names for removed categories on approved reports.
    const activeCategories = useMemo(
        () => allCategories.filter((category) => String(category.status).toLowerCase() === CATEGORY_STATUS.Active),
        [allCategories],
    );

    const canUsePreviewTab = activeTab === TAB.Reports || allowItemsPreview;

    const subTabOptions = useMemo<SubTabOption[]>(
        () => [
            { key: SUB_TAB.Preview, label: "Preview", isDisabled: activeTab === TAB.Items && !allowItemsPreview },
            { key: SUB_TAB.Review, label: "Review", isDisabled: false },
            { key: SUB_TAB.Completed, label: "Completed", isDisabled: false },
        ],
        [activeTab, allowItemsPreview],
    );

    const setActiveTab = useCallback(
        (nextTab: ActiveTab) => {
            setActiveTabState(nextTab);

            if (nextTab === TAB.Items) {
                setActiveSubTabState((previous) => (previous === SUB_TAB.Preview && !allowItemsPreview ? SUB_TAB.Review : previous));
            }
        },
        [allowItemsPreview, setActiveTabState, setActiveSubTabState],
    );

    const setActiveSubTab = useCallback(
        (nextSubTab: ActiveSubTab) => {
            if (activeTab === TAB.Items && nextSubTab === SUB_TAB.Preview && !allowItemsPreview) {
                return;
            }

            setActiveSubTabState(nextSubTab);
            setHasLoadedReports(false);
            setCurrentPage(0);
            cursorHistoryRef.current = [null];
            setReportColumnFilterState({});
            setReportSortState(null);
            // Reset the date range filter to its 30-day default on every sub-tab switch, consistent
            // with how sort/column filters are reset above.
            setReportDateRangeState(getDefaultReportDateRange());
            // Clear cached report expenses so the new sub-tab loads fresh expense states
            // (expenseStates from the new tab's ApproverReports response is the source of truth)
            setReportExpensesMap({});
            fetchedReportExpensesRef.current.clear();
            reportExpensesGenerationRef.current += 1;
        },
        [activeTab, allowItemsPreview, setActiveSubTabState],
    );

    const setPage = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);

    const setReportSort = useCallback((sort: { sortBy: ReportSortBy; sortOrder: "asc" | "desc" } | null) => {
        setReportSortState(sort);
        // Reset to first page whenever sort changes
        setCurrentPage(0);
        cursorHistoryRef.current = [null];
    }, []);

    const setReportColumnFilter = useCallback((filter: ApproverReportsColumnFilter) => {
        setReportColumnFilterState(filter);
        setCurrentPage(0);
        cursorHistoryRef.current = [null];
    }, []);

    const setReportDateRange = useCallback((range: ReportDateRange) => {
        smokeCheckReportDateRange(range, "setReportDateRange");
        setReportDateRangeState(range);
        setCurrentPage(0);
        cursorHistoryRef.current = [null];
    }, []);

    // Tracks the current sub-tab without adding it as a reactive effect dependency
    const activeSubTabRef = useRef(activeSubTab);
    activeSubTabRef.current = activeSubTab;

    // Tracks the current top-level tab without adding it as a reactive effect dependency
    const activeTabRef = useRef(activeTab);
    activeTabRef.current = activeTab;

    const store = useStore<RootState>();

    // Tracks latest report summaries without adding them as loadReportExpenses deps
    const reviewReportsRef = useRef(reviewReports);
    reviewReportsRef.current = reviewReports;
    const previewReportsRef = useRef(previewReports);
    previewReportsRef.current = previewReports;
    const completedReportsRef = useRef(completedReports);
    completedReportsRef.current = completedReports;

    /** Returns a flat list of all currently loaded reports across all sub-tabs. */
    const getAllReports = useCallback(
        () => [...(reviewReportsRef.current?.reports ?? []), ...(previewReportsRef.current?.reports ?? []), ...(completedReportsRef.current?.reports ?? [])],
        [],
    );

    /** Returns reports for the currently active reports sub-tab. */
    const getActiveSubTabReports = useCallback((): ExpenseReport[] => {
        if (activeSubTabRef.current === SUB_TAB.Preview) return previewReportsRef.current?.reports ?? [];
        if (activeSubTabRef.current === SUB_TAB.Completed) return completedReportsRef.current?.reports ?? [];
        return reviewReportsRef.current?.reports ?? [];
    }, []);

    /** Resolves the UUID (ExpenseReport.id) from an external report number (ExpenseReport.reportNumber).
     * Falls back to the input value if no match is found. */
    const resolveReportUuid = useCallback(
        (reportId: string): string => getAllReports().find((r) => r.reportNumber === reportId)?.id ?? reportId,
        [getAllReports],
    );
    const accessToken = useAppSelector(selectAccessToken);

    // Tracks in-flight requests to prevent StrictMode double-invocation
    const inflightRef = useRef<Set<string>>(new Set());

    // Build AppSync headers — stable helper to avoid repetition
    const appsyncHeaders = useCallback((): Record<string, string> => {
        const headers: Record<string, string> = {};
        if (APPSYNC_API_KEY) headers["x-api-key"] = APPSYNC_API_KEY;
        return headers;
    }, []);

    const gqlWithSharedBaseQuery = useCallback(
        async <T>(
            query: string,
            variables?: Record<string, unknown>,
            endpoint = GRAPHQL_ENDPOINT,
            requestHeaders: Record<string, string> = {},
            signal?: AbortSignal,
        ): Promise<T> => {
            const result = await executeGraphqlRequest(
                {
                    document: query,
                    variables,
                    endpoint,
                    headers: requestHeaders,
                    useDefaultApiKey: false,
                },
                {
                    getState: store.getState,
                    dispatch,
                    signal,
                },
            );

            if ("error" in result) {
                const firstError = result.error?.errors?.[0];
                if (firstError?.errorType === "AbortError") {
                    throw new DOMException(firstError.message, "AbortError");
                }
                throw new Error(firstError?.message ?? "GraphQL error");
            }

            if (result.data === undefined) {
                throw new Error("GraphQL returned no data");
            }

            return result.data as T;
        },
        [dispatch, store],
    );

    const locateReport = useCallback(
        async (identifier: string, subTab: ActiveSubTab): Promise<boolean> => {
            const category =
                subTab === SUB_TAB.Preview ? REPORT_CATEGORY.Preview : subTab === SUB_TAB.Completed ? REPORT_CATEGORY.Completed : REPORT_CATEGORY.Actionable;
            const filter = {
                category,
                view: APPROVER_REPORTS_VIEW,
                ...(subTab === SUB_TAB.Completed ? reportDateRangeRef.current : {}),
            };
            let searchFilter: typeof filter & { reportNumber?: string } = { ...filter, reportNumber: identifier };
            let cursor: string | undefined;
            let page = 0;
            const visitedCursors = new Set<string>();

            while (true) {
                const response = await gqlWithSharedBaseQuery<{
                    approverReports: { reports: ExpenseReport[]; summary: Omit<ReportsSummary, "reports">; pageInfo: ApproverReportsRawPageInfo };
                }>(APPROVER_REPORTS_QUERY, { limit: 10, cursor, filter: searchFilter }, APPSYNC_ENDPOINT, appsyncHeaders());
                const result = response.approverReports;
                const match = result.reports.find((report) => report.id === identifier || report.reportNumber === identifier);

                if (match) {
                    const summary = withReportPermissions({ reports: result.reports, ...result.summary }, subTab);
                    if (subTab === SUB_TAB.Preview) setPreviewReports(summary);
                    else if (subTab === SUB_TAB.Completed) setCompletedReports(summary);
                    else setReviewReports(summary);
                    setCurrentPage(page);
                    setPageInfo({
                        offset: page * result.pageInfo.limit,
                        limit: result.pageInfo.limit,
                        totalCount: result.pageInfo.totalCount,
                        hasNextPage: result.pageInfo.hasNextPage,
                    });
                    if (result.pageInfo.cursor != null) cursorHistoryRef.current[page + 1] = result.pageInfo.cursor;
                    return true;
                }

                if (searchFilter.reportNumber) {
                    searchFilter = filter;
                    cursor = undefined;
                    page = 0;
                    continue;
                }

                if (!result.pageInfo.hasNextPage || result.pageInfo.cursor == null || visitedCursors.has(result.pageInfo.cursor)) return false;
                visitedCursors.add(result.pageInfo.cursor);
                cursor = result.pageInfo.cursor;
                page += 1;
            }
        },
        [appsyncHeaders, gqlWithSharedBaseQuery],
    );

    // Fetches only the paginated approver reports — re-runs when the page changes
    // and after mutations that affect the review tab.
    const loadReviewReports = useCallback(async () => {
        if (!accessToken || inflightRef.current.has("review-reports")) return;
        inflightRef.current.add("review-reports");
        setIsLoading(true);
        setError(null);
        try {
            const cursor = cursorHistoryRef.current[currentPage] ?? undefined;
            const rr = await gqlWithSharedBaseQuery<{
                approverReports: { reports: ExpenseReport[]; summary: Omit<ReportsSummary, "reports">; pageInfo: ApproverReportsRawPageInfo };
            }>(
                APPROVER_REPORTS_QUERY,
                {
                    limit: 10,
                    cursor,
                    filter: { category: REPORT_CATEGORY.Actionable, view: APPROVER_REPORTS_VIEW, ...reportSortRef.current, ...reportColumnFilterRef.current },
                },
                APPSYNC_ENDPOINT,
                appsyncHeaders(),
            );
            setReviewReports(withReportPermissions({ reports: rr.approverReports.reports, ...rr.approverReports.summary }, SUB_TAB.Review));
            setHasLoadedReports(true);
            const rawPageInfo = rr.approverReports.pageInfo;
            if (rawPageInfo.cursor != null) cursorHistoryRef.current[currentPage + 1] = rawPageInfo.cursor;
            setPageInfo({
                offset: currentPage * rawPageInfo.limit,
                limit: rawPageInfo.limit,
                totalCount: rawPageInfo.totalCount,
                hasNextPage: rawPageInfo.hasNextPage,
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load review reports");
        } finally {
            setIsLoading(false);
            inflightRef.current.delete("review-reports");
        }
    }, [accessToken, appsyncHeaders, currentPage, gqlWithSharedBaseQuery]);

    const loadPreviewReports = useCallback(async () => {
        if (!accessToken || inflightRef.current.has("preview-reports")) return;
        inflightRef.current.add("preview-reports");
        setIsLoading(true);
        setError(null);
        try {
            const cursor = cursorHistoryRef.current[currentPage] ?? undefined;
            const pr = await gqlWithSharedBaseQuery<{
                approverReports: { reports: ExpenseReport[]; summary: Omit<ReportsSummary, "reports">; pageInfo: ApproverReportsRawPageInfo };
            }>(
                APPROVER_REPORTS_QUERY,
                {
                    limit: 10,
                    cursor,
                    filter: { category: REPORT_CATEGORY.Preview, view: APPROVER_REPORTS_VIEW, ...reportSortRef.current, ...reportColumnFilterRef.current },
                },
                APPSYNC_ENDPOINT,
                appsyncHeaders(),
            );
            setPreviewReports(withReportPermissions({ reports: pr.approverReports.reports, ...pr.approverReports.summary }, SUB_TAB.Preview));
            setHasLoadedReports(true);
            const rawPageInfo = pr.approverReports.pageInfo;
            if (rawPageInfo.cursor != null) cursorHistoryRef.current[currentPage + 1] = rawPageInfo.cursor;
            setPageInfo({
                offset: currentPage * rawPageInfo.limit,
                limit: rawPageInfo.limit,
                totalCount: rawPageInfo.totalCount,
                hasNextPage: rawPageInfo.hasNextPage,
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load preview reports");
        } finally {
            setIsLoading(false);
            inflightRef.current.delete("preview-reports");
        }
    }, [accessToken, appsyncHeaders, currentPage, gqlWithSharedBaseQuery]);

    const loadCompletedReports = useCallback(async () => {
        if (!accessToken || inflightRef.current.has("completed-reports")) return;
        inflightRef.current.add("completed-reports");
        setIsLoading(true);
        setError(null);
        try {
            const cursor = cursorHistoryRef.current[currentPage] ?? undefined;
            smokeCheckReportDateRange(reportDateRangeRef.current, "loadCompletedReports");
            const cr = await gqlWithSharedBaseQuery<{
                approverReports: { reports: ExpenseReport[]; summary: Omit<ReportsSummary, "reports">; pageInfo: ApproverReportsRawPageInfo };
            }>(
                APPROVER_REPORTS_QUERY,
                {
                    limit: 10,
                    cursor,
                    filter: {
                        category: REPORT_CATEGORY.Completed,
                        view: APPROVER_REPORTS_VIEW,
                        ...reportSortRef.current,
                        ...reportColumnFilterRef.current,
                        ...reportDateRangeRef.current,
                    },
                },
                APPSYNC_ENDPOINT,
                appsyncHeaders(),
            );
            setCompletedReports(withReportPermissions({ reports: cr.approverReports.reports, ...cr.approverReports.summary }, SUB_TAB.Completed));
            setHasLoadedReports(true);
            const rawPageInfo = cr.approverReports.pageInfo;
            if (rawPageInfo.cursor != null) cursorHistoryRef.current[currentPage + 1] = rawPageInfo.cursor;
            setPageInfo({
                offset: currentPage * rawPageInfo.limit,
                limit: rawPageInfo.limit,
                totalCount: rawPageInfo.totalCount,
                hasNextPage: rawPageInfo.hasNextPage,
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load completed reports");
        } finally {
            setIsLoading(false);
            inflightRef.current.delete("completed-reports");
        }
    }, [accessToken, appsyncHeaders, currentPage, gqlWithSharedBaseQuery]);

    const exportCompletedReports = useCallback(async (): Promise<void> => {
        if (!accessToken) return;
        setIsExporting(true);
        setError(null);
        try {
            const { updatedDateFrom, updatedDateTo } = reportDateRangeRef.current;
            smokeCheckReportDateRange(reportDateRangeRef.current, "exportCompletedReports");
            const data = await gqlWithSharedBaseQuery<{ exportApproverReports: string }>(
                EXPORT_APPROVER_REPORTS_QUERY,
                { filter: { updatedDateFrom, updatedDateTo } },
                APPSYNC_ENDPOINT,
                appsyncHeaders(),
            );
            const csvBytes = decodeBase64ToBytes(data.exportApproverReports);
            downloadCsvFile(csvBytes, `completed-reports_${updatedDateFrom}_to_${updatedDateTo}.csv`);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to export reports");
        } finally {
            setIsExporting(false);
        }
    }, [accessToken, appsyncHeaders, gqlWithSharedBaseQuery]);

    const loadReviewItems = useCallback(async () => {
        if (!accessToken || reviewItems !== null || inflightRef.current.has("review-items")) return;
        inflightRef.current.add("review-items");
        setIsLoading(true);
        try {
            const ri = await gqlWithSharedBaseQuery<{ reviewItems: ItemsSummary }>(REVIEW_ITEMS_QUERY);
            setReviewItems(withItemPermissions(ri.reviewItems, SUB_TAB.Review));
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load review items");
        } finally {
            setIsLoading(false);
            inflightRef.current.delete("review-items");
        }
    }, [accessToken, gqlWithSharedBaseQuery, reviewItems]);

    const loadCompletedItems = useCallback(async () => {
        if (!accessToken || completedItems !== null || inflightRef.current.has("completed-items")) return;
        inflightRef.current.add("completed-items");
        setIsLoading(true);
        try {
            const ci = await gqlWithSharedBaseQuery<{ completedItems: ItemsSummary }>(COMPLETED_ITEMS_QUERY);
            setCompletedItems(withItemPermissions(ci.completedItems, SUB_TAB.Completed));
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load completed items");
        } finally {
            setIsLoading(false);
            inflightRef.current.delete("completed-items");
        }
    }, [accessToken, completedItems, gqlWithSharedBaseQuery]);

    /** Lazy-loads expenses for a single report on first expand. Idempotent — safe to call repeatedly. */
    const loadReportExpenses = useCallback(
        async (reportId: string) => {
            const generationAtStart = reportExpensesGenerationRef.current;
            const key = `report-expenses:${reportId}:g${generationAtStart}`;
            if (!accessToken || fetchedReportExpensesRef.current.has(reportId) || inflightRef.current.has(key)) return;
            inflightRef.current.add(key);
            try {
                const data = await gqlWithSharedBaseQuery<{ reportExpenses: { items: ReportExpenseItemNetwork[]; summary: ReportExpensesData["summary"] } }>(
                    REPORT_EXPENSES_QUERY,
                    { reportId },
                    APPSYNC_ENDPOINT,
                    appsyncHeaders(),
                );
                if (generationAtStart !== reportExpensesGenerationRef.current) {
                    // Ignore responses from a stale sub-tab context.
                    return;
                }
                fetchedReportExpensesRef.current.add(reportId);
                // TODO: remove this override once reportExpenses returns the correct per-expense
                // status directly. Currently the reportExpenses query returns stale statuses, so
                // we use expenseStates from the parent approverReports report as the source of
                // truth for the initial displayed state.
                const report = getActiveSubTabReports().find((r) => r.id === reportId) ?? getAllReports().find((r) => r.id === reportId);
                const stateMap = new Map((report?.expenseStates ?? []).map((es) => [es.expenseId, es.state]));
                const items = data.reportExpenses.items.map((item) => ({
                    ...item,
                    status: stateMap.get(item.id) ?? ItemStatusValue.Pending,
                }));
                setReportExpensesMap((prev) => ({ ...prev, [reportId]: { ...data.reportExpenses, items } }));
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to load report expenses");
            } finally {
                inflightRef.current.delete(key);
            }
        },
        [accessToken, appsyncHeaders, getActiveSubTabReports, getAllReports, gqlWithSharedBaseQuery],
    );

    const loadUiConfig = useCallback(async () => {
        if (!accessToken || inflightRef.current.has("ui-config")) return;
        inflightRef.current.add("ui-config");
        try {
            const uiConfig = await gqlWithSharedBaseQuery<{ approvalUiConfig: { allowItemsPreview: boolean } }>(
                APPROVAL_UI_CONFIG_QUERY,
                undefined,
                APPSYNC_ENDPOINT,
                appsyncHeaders(),
            );
            setAllowItemsPreview(uiConfig.approvalUiConfig.allowItemsPreview);
        } catch {
            // Non-critical — leave allowItemsPreview false
        } finally {
            inflightRef.current.delete("ui-config");
        }
    }, [accessToken, appsyncHeaders, gqlWithSharedBaseQuery]);

    const loadAllCategories = useCallback(async () => {
        if (!accessToken || inflightRef.current.has("all-categories")) return;
        inflightRef.current.add("all-categories");
        setCategoriesStatus(CATEGORIES_LOAD_STATUS.Loading);
        try {
            const collected: Category[] = [];
            let cursor: string | null = null;
            let hasNextPage = true;

            while (hasNextPage) {
                const data: AllCategoriesResponse = await gqlWithSharedBaseQuery<AllCategoriesResponse>(
                    ALL_CATEGORIES_QUERY,
                    {
                        limit: ALL_CATEGORIES_PAGE_LIMIT,
                        cursor,
                    },
                    APPSYNC_ENDPOINT,
                    appsyncHeaders(),
                );

                collected.push(...data.approvalCategories.items);
                hasNextPage = data.approvalCategories.pageInfo.hasNextPage;
                cursor = data.approvalCategories.pageInfo.cursor;

                if (!hasNextPage || !cursor) break;
            }

            setAllCategories(collected);
            setCategoriesStatus(CATEGORIES_LOAD_STATUS.Ready);
        } catch {
            // Approvals stay blocked until the list loads; the page offers a retry.
            setCategoriesStatus(CATEGORIES_LOAD_STATUS.Error);
        } finally {
            inflightRef.current.delete("all-categories");
        }
    }, [accessToken, appsyncHeaders, gqlWithSharedBaseQuery]);

    const getLocationPredictions = useCallback(
        async (input: string): Promise<Array<{ description: string; placeId: string | null }>> => {
            if (input.trim().length < 3) return [];
            try {
                const data = await gqlWithSharedBaseQuery<{
                    locationPredictions?: { predictions?: Array<{ description: string; placeId: string | null }> } | null;
                }>(LOCATION_PREDICTIONS_QUERY, { input: input.trim() }, APPSYNC_ENDPOINT, appsyncHeaders());
                return data.locationPredictions?.predictions ?? [];
            } catch {
                return [];
            }
        },
        [appsyncHeaders, gqlWithSharedBaseQuery],
    );

    // On auth: load approver reports (default view), ui config (needed for tab rendering),
    // and the complete category list (needed for category name display in expanded reports).
    useEffect(() => {
        void loadReviewReports();
        void loadUiConfig();
        void loadAllCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]); // intentional: only re-run when token changes, not on every loadReviewReports identity change

    // Re-run the active report sub-tab when page, sort, or column filter changes (but NOT on first mount — handled above).
    // Preview and completed are re-triggered via the lazy-load effect (their callbacks capture currentPage);
    // review is handled here explicitly because it is intentionally omitted from the lazy-load deps.
    // currentPage, reportSort, reportColumnFilter, and reportDateRange are combined into one effect to
    // avoid double-fetching when their setters reset the page simultaneously.
    // Guard: skip when the user is on the Items tab — page/sort changes there should not trigger
    // a background report fetch that clobbers isLoading / pageInfo state.
    const isFirstMount = useState(true);
    useEffect(() => {
        if (isFirstMount[0]) {
            isFirstMount[1](false);
            return;
        }
        if (activeTabRef.current !== TAB.Reports) return;
        if (activeSubTabRef.current === SUB_TAB.Preview) void loadPreviewReports();
        else if (activeSubTabRef.current === SUB_TAB.Completed) void loadCompletedReports();
        else void loadReviewReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, reportSort, reportColumnFilter, reportDateRange]);

    // Lazy-load data when the user switches to a tab that hasn't been fetched yet
    useEffect(() => {
        if (activeTab === TAB.Reports && activeSubTab === SUB_TAB.Preview) void loadPreviewReports();
        if (activeTab === TAB.Reports && activeSubTab === SUB_TAB.Review) void loadReviewReports();
        if (activeTab === TAB.Reports && activeSubTab === SUB_TAB.Completed) void loadCompletedReports();
        if (activeTab === TAB.Items && activeSubTab === SUB_TAB.Review) void loadReviewItems();
        if (activeTab === TAB.Items && activeSubTab === SUB_TAB.Completed) void loadCompletedItems();
    }, [activeTab, activeSubTab, loadPreviewReports, loadReviewReports, loadCompletedReports, loadReviewItems, loadCompletedItems]);

    // Load full expense detail when an item is selected
    useEffect(() => {
        if (!selectedItemId) {
            setFullExpenseDetail(null);
            setIsLoadingReceipt(false);
            return;
        }

        if (savedViolationsRef.current && savedViolationsRef.current.itemId !== selectedItemId) savedViolationsRef.current = null;

        // Find the item in current data to get reportId + submitterName
        const allItems = [...(reviewItems?.items ?? []), ...(completedItems?.items ?? [])];
        const found = allItems.find((i) => i.id === selectedItemId) ?? null;
        setSelectedItem(found);

        // Also search across all report expenses for items opened from the reports tab
        const allReportItems = Object.values(reportExpensesMap).flatMap((d) => d.items);
        const fromReport = allReportItems.find((i) => i.id === selectedItemId) ?? null;
        const anchor = found ?? fromReport;
        if (!anchor) return;

        const isBackgroundRefresh = fullExpenseDetailIdRef.current === selectedItemId;
        const abortController = new AbortController();
        if (!isBackgroundRefresh) {
            setFullExpenseDetail(null);
            setIsLoadingReceipt(true);
        }

        gqlWithSharedBaseQuery<{ getReportFullExpenses: RawFullExpenseDetailResponse }>(
            GET_REPORT_FULL_EXPENSES_QUERY,
            { reportId: anchor.reportId.replace(/^Report#/, ""), expenseId: selectedItemId },
            APPSYNC_ENDPOINT,
            appsyncHeaders(),
            abortController.signal,
        )
            .then((d) => {
                const wrapper = d.getReportFullExpenses;
                const item = wrapper.items[0] ?? null;
                if (!item) {
                    setFullExpenseDetail(null);
                    return;
                }
                // TODO: revert to wrapper.reportNumber / wrapper.submittedBy once
                // GetReportFullExpenses does NOT currently return the correct submitter name
                // or report number. Pulling from the ApproverReports parent report instead.
                // Using getAllReports() (ref-based) avoids adding report state arrays to the
                // effect deps, which would cause unnecessary re-fetches on every item mutation.
                const cleanReportId = anchor.reportId.replace(/^Report#/, "");
                const parentReport = getAllReports().find((r) => r.id === cleanReportId);
                // TODO: remove this status override once getReportFullExpenses returns the correct
                // per-expense status directly. expenseStates from the parent approverReports report
                // is the authoritative source of truth until the GraphQL layer is fixed.
                const expenseState = parentReport?.expenseStates?.find((es) => es.expenseId === item.id);
                // When expenseStates is present (even empty), it is authoritative; absent entries default to PENDING.
                const statusFromParent = parentReport?.expenseStates != null ? (expenseState?.state ?? ItemStatusValue.Pending) : anchor.status;
                // Use the schema's actual field names (note the backend typo `milage*`).
                // `item` is already typed as `RawFullExpenseItem`/`FullExpenseDetail`,
                // so access the properties directly to preserve type-safety.
                const milageLocationFrom = item.milageLocationFrom ?? null;
                const milageLocationTo = item.milageLocationTo ?? null;
                const localItem = found ?? fromReport;
                setFullExpenseDetail({
                    ...item,
                    category: localItem?.category ?? item.category,
                    subCategoryId: found ? found.subCategoryId : item.subCategoryId,
                    violations: savedViolationsRef.current?.itemId === item.id ? savedViolationsRef.current.violations : item.violations,
                    milageLocationFrom,
                    milageLocationTo,
                    status: statusFromParent,
                    reportState: parentReport?.state ?? null,
                    reportNumber: parentReport?.reportNumber ?? wrapper.reportNumber,
                    submittedBy: parentReport ? `${parentReport.submitter.firstName} ${parentReport.submitter.lastName}`.trim() : wrapper.submittedBy,
                    submittedAt: parentReport?.submittedAt ?? wrapper.submittedAt ?? null,
                });
            })
            .catch((error: unknown) => {
                if ((error instanceof Error || error instanceof DOMException) && error.name === "AbortError") return;
                if (!isBackgroundRefresh) setFullExpenseDetail(null);
            })
            .finally(() => {
                if (!abortController.signal.aborted && !isBackgroundRefresh) setIsLoadingReceipt(false);
            });

        return () => abortController.abort();
    }, [selectedItemId, reviewItems, completedItems, reportExpensesMap, getAllReports, appsyncHeaders, gqlWithSharedBaseQuery]);

    // Patches expense statuses and the parent report status immediately after a
    // successful updateExpenseStatus mutation — no refetch needed.
    const applyStatusUpdate = useCallback((updatedItems: Array<{ expenseId: string; newStatus: ItemStatus }>, reportId: string, reportStatus: ReportStatus) => {
        const statusMap = new Map(updatedItems.map((u) => [u.expenseId, u.newStatus]));

        // TODO: remove expenseStates patching here once reportExpenses returns correct statuses.
        // This keeps expenseStates in sync with local mutations so that expanding a report after
        // a mutation does not re-apply stale statuses from the original approverReports response.
        const patchReport = (r: ExpenseReport) => {
            if (r.id !== reportId) return r;
            const existing = r.expenseStates ?? [];
            const updatedStates = existing.map((es) => {
                const updated = statusMap.get(es.expenseId);
                return updated ? { ...es, state: updated } : es;
            });
            const existingIds = new Set(existing.map((es) => es.expenseId));
            for (const [expenseId, state] of statusMap) {
                if (!existingIds.has(expenseId)) {
                    updatedStates.push({ expenseId, state });
                }
            }
            return {
                ...r,
                state: reportStatus,
                expenseStates: updatedStates,
            };
        };

        setReviewReports((prev) => (prev ? { ...prev, reports: prev.reports.map(patchReport) } : prev));
        setPreviewReports((prev) => (prev ? { ...prev, reports: prev.reports.map(patchReport) } : prev));
        setCompletedReports((prev) => (prev ? { ...prev, reports: prev.reports.map(patchReport) } : prev));
        setReviewItems((prev) =>
            prev
                ? {
                      ...prev,
                      items: prev.items.map((i) => {
                          const s = statusMap.get(i.id);
                          return s ? { ...i, status: s } : i;
                      }),
                  }
                : prev,
        );
        setCompletedItems((prev) =>
            prev
                ? {
                      ...prev,
                      items: prev.items.map((i) => {
                          const s = statusMap.get(i.id);
                          return s ? { ...i, status: s } : i;
                      }),
                  }
                : prev,
        );
        setReportExpensesMap((prev) => {
            const next = { ...prev };
            for (const [key, val] of Object.entries(next)) {
                const updated = val.items.map((i) => {
                    const s = statusMap.get(i.id);
                    return s ? { ...i, status: s } : i;
                });
                if (updated.some((i, idx) => i !== val.items[idx])) {
                    next[key] = { ...val, items: updated };
                }
            }
            return next;
        });
        setFullExpenseDetail((prev) => {
            if (!prev) return prev;
            const s = statusMap.get(prev.id);
            return s ? { ...prev, status: s } : prev;
        });
    }, []);

    const findReportIdForItem = useCallback(
        (itemId: string): string => {
            const allItems = [...(reviewItems?.items ?? []), ...(completedItems?.items ?? [])];
            const fromItems = allItems.find((i) => i.id === itemId);
            const fromMap = Object.values(reportExpensesMap)
                .flatMap((d) => d.items)
                .find((i) => i.id === itemId);
            const raw = fromItems?.reportId ?? fromMap?.reportId ?? fullExpenseDetail?.reportId ?? "";
            return raw.replace(/^Report#/, "");
        },
        [reviewItems, completedItems, reportExpensesMap, fullExpenseDetail],
    );

    const findSubmitterUserIdForItem = useCallback(
        (itemId: string): string => {
            const allItems = [...(reviewItems?.items ?? []), ...(completedItems?.items ?? [])];
            const fromItems = allItems.find((i) => i.id === itemId);
            if (fromItems?.submitterUserId) return fromItems.submitterUserId;

            const reportId = findReportIdForItem(itemId);
            const report = getAllReports().find((r) => r.id === reportId);
            return report?.submitterUserId ?? "";
        },
        [reviewItems, completedItems, findReportIdForItem, getAllReports],
    );

    const approveItem = useCallback(
        async (itemId: string) => {
            const reportId = findReportIdForItem(itemId);
            setIsMutating(true);
            setProcessingItemId(itemId);
            setProcessingAction(ExpenseAction.Approve);
            try {
                const result = await gqlWithSharedBaseQuery<{
                    updateExpenseStatus: { reportId: string; items: Array<{ expenseId: string; newStatus: ItemStatus }>; reportStatus: ReportStatus };
                }>(UPDATE_EXPENSE_STATUS_MUTATION, { reportId, expenseIds: [itemId], action: ExpenseAction.Approve }, APPSYNC_ENDPOINT, appsyncHeaders());
                applyStatusUpdate(
                    result.updateExpenseStatus.items,
                    resolveReportUuid(result.updateExpenseStatus.reportId),
                    result.updateExpenseStatus.reportStatus,
                );
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to approve item");
            } finally {
                setIsMutating(false);
                setProcessingItemId(null);
                setProcessingAction(null);
            }
        },
        [findReportIdForItem, resolveReportUuid, applyStatusUpdate, appsyncHeaders, gqlWithSharedBaseQuery],
    );

    const approveItems = useCallback(
        async (itemIds: string[]) => {
            setIsMutating(true);
            try {
                await gqlWithSharedBaseQuery(APPROVE_ITEMS_MUTATION, { itemIds });
                await loadReviewReports();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to approve items");
            } finally {
                setIsMutating(false);
            }
        },
        [gqlWithSharedBaseQuery, loadReviewReports],
    );

    const denyItem = useCallback(
        async (itemId: string) => {
            const reportId = findReportIdForItem(itemId);
            setIsMutating(true);
            setProcessingItemId(itemId);
            setProcessingAction(ExpenseAction.Deny);
            try {
                const result = await gqlWithSharedBaseQuery<{
                    updateExpenseStatus: { reportId: string; items: Array<{ expenseId: string; newStatus: ItemStatus }>; reportStatus: ReportStatus };
                }>(UPDATE_EXPENSE_STATUS_MUTATION, { reportId, expenseIds: [itemId], action: ExpenseAction.Deny }, APPSYNC_ENDPOINT, appsyncHeaders());
                applyStatusUpdate(
                    result.updateExpenseStatus.items,
                    resolveReportUuid(result.updateExpenseStatus.reportId),
                    result.updateExpenseStatus.reportStatus,
                );
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to deny item");
            } finally {
                setIsMutating(false);
                setProcessingItemId(null);
                setProcessingAction(null);
            }
        },
        [findReportIdForItem, resolveReportUuid, applyStatusUpdate, appsyncHeaders, gqlWithSharedBaseQuery],
    );

    const pendingItem = useCallback(
        async (itemId: string) => {
            const reportId = findReportIdForItem(itemId);
            setIsMutating(true);
            setProcessingItemId(itemId);
            setProcessingAction(ExpenseAction.Pending);
            try {
                const result = await gqlWithSharedBaseQuery<{
                    updateExpenseStatus: { reportId: string; items: Array<{ expenseId: string; newStatus: ItemStatus }>; reportStatus: ReportStatus };
                }>(UPDATE_EXPENSE_STATUS_MUTATION, { reportId, expenseIds: [itemId], action: ExpenseAction.Pending }, APPSYNC_ENDPOINT, appsyncHeaders());
                applyStatusUpdate(
                    result.updateExpenseStatus.items,
                    resolveReportUuid(result.updateExpenseStatus.reportId),
                    result.updateExpenseStatus.reportStatus,
                );
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to set item to pending");
            } finally {
                setIsMutating(false);
                setProcessingItemId(null);
                setProcessingAction(null);
            }
        },
        [findReportIdForItem, resolveReportUuid, applyStatusUpdate, appsyncHeaders, gqlWithSharedBaseQuery],
    );

    const updateExpenseCategory = useCallback(
        async (itemId: string, selectedCategory: Category, fieldOverrides: Record<string, unknown> = {}): Promise<UpdateExpenseCategoryResult> => {
            const reportId = findReportIdForItem(itemId);
            const submitterUserId = findSubmitterUserIdForItem(itemId);
            const expenseDetail = fullExpenseDetail?.id === itemId ? fullExpenseDetail : null;

            if (!reportId) {
                setError("Missing report ID for expense update");
                return { success: false, message: "Missing report ID for expense update" };
            }

            if (!submitterUserId) {
                setError("Missing submitter user ID for expense update");
                return { success: false, message: "Missing submitter user ID for expense update" };
            }

            if (!expenseDetail) {
                setError("Missing expense detail for expense update");
                return { success: false, message: "Missing expense detail for expense update" };
            }

            if (!selectedCategory.categoryId?.trim()) {
                setError("Missing category ID for expense update");
                return { success: false, message: "Missing category ID for expense update" };
            }

            const matchedCategory = activeCategories.find((category) => category.categoryId === selectedCategory.categoryId) ?? selectedCategory;

            const expenseCategoryId = resolveExpenseCategoryId(matchedCategory);
            if (expenseCategoryId == null) {
                const message = `Unable to resolve category IDs for "${matchedCategory.categoryName}".`;
                setError(message);
                return { success: false, message };
            }

            setIsMutating(true);
            try {
                const result = await gqlWithSharedBaseQuery<{
                    updateExpense: { success: boolean; reportId: string; expenseId: string; message: string | null; violations?: PolicyViolation[] | null };
                }>(
                    UPDATE_EXPENSE_MUTATION,
                    {
                        reportId,
                        expenseId: itemId,
                        submitterUserId,
                        expense: buildUpdateExpenseInputFromSelectedCategory(expenseDetail, matchedCategory, expenseCategoryId, fieldOverrides),
                    },
                    APPSYNC_ENDPOINT,
                    appsyncHeaders(),
                );

                if (!result.updateExpense.success) {
                    const message = result.updateExpense.message ?? "Failed to update expense category";
                    setError(message);
                    return { success: false, message };
                }

                showSuccess("Expense category updated", result.updateExpense.message ?? "The expense category changes were saved.");

                const violations = result.updateExpense.violations ?? undefined;
                savedViolationsRef.current = violations ? { itemId, violations } : null;
                const categoryPatch: CategoryStatePatch = { ...buildCategoryStatePatch(matchedCategory), violations };
                const applyCategoryPatch = () => {
                    setFullExpenseDetail((prev) => {
                        if (!prev || prev.id !== itemId) return prev;
                        return {
                            ...prev,
                            subCategoryId: categoryPatch.subCategoryId,
                            category: categoryPatch.category,
                            ...(violations ? { violations } : {}),
                        };
                    });

                    setReviewItems((prev) => (prev ? { ...prev, items: patchExpenseItemCategory(prev.items, itemId, categoryPatch) } : prev));
                    setCompletedItems((prev) => (prev ? { ...prev, items: patchExpenseItemCategory(prev.items, itemId, categoryPatch) } : prev));

                    setReportExpensesMap((prev) => {
                        const existing = prev[reportId];
                        if (!existing) return prev;
                        return {
                            ...prev,
                            [reportId]: {
                                ...existing,
                                items: patchReportExpenseCategory(existing.items, itemId, categoryPatch),
                            },
                        };
                    });
                };

                applyCategoryPatch();

                // Refresh the expanded report; re-apply the patch because reportExpenses may return stale category labels.
                fetchedReportExpensesRef.current.delete(reportId);
                await loadReportExpenses(reportId);
                applyCategoryPatch();

                return { success: true, message: result.updateExpense.message ?? null, violations: violations ?? [] };
            } catch (err) {
                const message = err instanceof Error ? err.message : "Failed to update expense category";
                setError(message);
                return { success: false, message };
            } finally {
                setIsMutating(false);
            }
        },
        [
            findReportIdForItem,
            findSubmitterUserIdForItem,
            fullExpenseDetail,
            gqlWithSharedBaseQuery,
            appsyncHeaders,
            activeCategories,
            showSuccess,
            loadReportExpenses,
        ],
    );

    const denyItems = useCallback(
        async (itemIds: string[]) => {
            setIsMutating(true);
            try {
                await gqlWithSharedBaseQuery(DENY_ITEMS_MUTATION, { itemIds });
                await loadReviewReports();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to deny items");
            } finally {
                setIsMutating(false);
            }
        },
        [gqlWithSharedBaseQuery, loadReviewReports],
    );

    const approveReport = useCallback(
        async (reportId: string, message?: string) => {
            setIsMutating(true);
            setIsProcessingReport(true);
            try {
                await gqlWithSharedBaseQuery(UPDATE_REPORT_MUTATION, { reportId, action: ReportActionValue.Approve }, APPSYNC_ENDPOINT, appsyncHeaders());
                // Optionally create a report-level comment when the accountant provided a message
                if (message && message.trim().length > 0) {
                    try {
                        await gqlWithSharedBaseQuery(
                            CREATE_REPORT_COMMENT_MUTATION,
                            { reportId, input: { text: message.trim() } },
                            APPSYNC_ENDPOINT,
                            appsyncHeaders(),
                        );
                    } catch {
                        // non-fatal — swallow comment errors but keep report update
                    }
                }
                await loadReviewReports();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to approve report");
            } finally {
                setIsMutating(false);
                setIsProcessingReport(false);
            }
        },
        [loadReviewReports, appsyncHeaders, gqlWithSharedBaseQuery],
    );

    const denyReport = useCallback(
        async (reportId: string, message?: string) => {
            setIsMutating(true);
            setIsProcessingReport(true);
            try {
                await gqlWithSharedBaseQuery(UPDATE_REPORT_MUTATION, { reportId, action: ReportActionValue.SendBack }, APPSYNC_ENDPOINT, appsyncHeaders());
                if (message && message.trim().length > 0) {
                    try {
                        await gqlWithSharedBaseQuery(
                            CREATE_REPORT_COMMENT_MUTATION,
                            { reportId, input: { text: message.trim() } },
                            APPSYNC_ENDPOINT,
                            appsyncHeaders(),
                        );
                    } catch {
                        // swallow
                    }
                }
                await loadReviewReports();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to deny report");
            } finally {
                setIsMutating(false);
                setIsProcessingReport(false);
            }
        },
        [loadReviewReports, appsyncHeaders, gqlWithSharedBaseQuery],
    );

    const getReportComments = useCallback(
        async (reportId: string) => {
            const data = await gqlWithSharedBaseQuery<{ getReportComments: { items: Array<Record<string, unknown>> } }>(
                GET_REPORT_COMMENTS_QUERY,
                { reportId },
                APPSYNC_ENDPOINT,
                appsyncHeaders(),
            );
            return data.getReportComments.items as Array<{
                commentId: string;
                authorId: string;
                authorFullName: string;
                authorRoles: string[];
                text: string;
                deleted: boolean;
                createdAt: string;
            }>;
        },
        [appsyncHeaders, gqlWithSharedBaseQuery],
    );

    const createReportComment = useCallback(
        async (reportId: string, text: string) => {
            try {
                const res = await gqlWithSharedBaseQuery<{ createReportComment: Record<string, unknown> }>(
                    CREATE_REPORT_COMMENT_MUTATION,
                    { reportId, input: { text: text.trim() } },
                    APPSYNC_ENDPOINT,
                    appsyncHeaders(),
                );
                return res.createReportComment as {
                    commentId: string;
                    authorId: string;
                    authorRoles: string[];
                    text: string;
                    deleted: boolean;
                    createdAt: string;
                };
            } catch {
                return null;
            }
        },
        [appsyncHeaders, gqlWithSharedBaseQuery],
    );

    const getExpenseComments = useCallback(
        async (expenseId: string) => {
            const data = await gqlWithSharedBaseQuery<{ getExpenseComments: { items: Array<Record<string, unknown>> } }>(
                GET_EXPENSE_COMMENTS_QUERY,
                { expenseId },
                APPSYNC_ENDPOINT,
                appsyncHeaders(),
            );
            return data.getExpenseComments.items as Array<{
                commentId: string;
                authorId: string;
                authorFullName: string;
                authorRoles: string[];
                text: string;
                deleted: boolean;
                createdAt: string;
            }>;
        },
        [appsyncHeaders, gqlWithSharedBaseQuery],
    );

    const getPolicyViolationComments = useCallback(
        async (violationId: string) => {
            const data = await gqlWithSharedBaseQuery<{ getPolicyViolationComments: { items: Array<Record<string, unknown>> } }>(
                GET_POLICY_VIOLATION_COMMENTS_QUERY,
                { violationId },
                APPSYNC_ENDPOINT,
                appsyncHeaders(),
            );
            return data.getPolicyViolationComments.items as Array<{
                commentId: string;
                authorId: string;
                authorFullName: string;
                authorRoles: string[];
                text: string;
                deleted: boolean;
                createdAt: string;
            }>;
        },
        [appsyncHeaders, gqlWithSharedBaseQuery],
    );

    const createExpenseComment = useCallback(
        async (expenseId: string, text: string) => {
            try {
                const res = await gqlWithSharedBaseQuery<{ createExpenseComment: Record<string, unknown> }>(
                    CREATE_EXPENSE_COMMENT_MUTATION,
                    { expenseId, input: { text: text.trim() } },
                    APPSYNC_ENDPOINT,
                    appsyncHeaders(),
                );
                return res.createExpenseComment as {
                    commentId: string;
                    authorId: string;
                    authorRoles: string[];
                    text: string;
                    deleted: boolean;
                    createdAt: string;
                };
            } catch {
                return null;
            }
        },
        [appsyncHeaders, gqlWithSharedBaseQuery],
    );

    const getExpenseHistory = useCallback(
        async (expenseId: string) => {
            const data = await gqlWithSharedBaseQuery<{ getExpenseHistory: { items: Array<Record<string, unknown>> } }>(
                GET_EXPENSE_HISTORY_QUERY,
                { expenseId },
                APPSYNC_ENDPOINT,
                appsyncHeaders(),
            );
            return data.getExpenseHistory.items as Array<{
                id: string;
                event: string;
                eventDisplay: string;
                occurredAt: string;
                actor: { type: string; userId: string; displayName: string } | null;
            }>;
        },
        [appsyncHeaders, gqlWithSharedBaseQuery],
    );

    return {
        activeTab,
        setActiveTab,
        activeSubTab,
        setActiveSubTab,
        canUsePreviewTab,
        subTabOptions,
        previewReports,
        reviewReports,
        completedReports,
        reviewItems: reviewItems ? withItemPermissions(reviewItems, "review") : null,
        completedItems: completedItems ? withItemPermissions(completedItems, "completed") : null,
        selectedItemId,
        setSelectedItemId,
        selectedItem,
        fullExpenseDetail,
        isLoadingReceipt,
        isLoading,
        hasLoadedReports,
        isMutating,
        isProcessingReport,
        processingItemId: processingItemId,
        processingAction,
        error,
        currentPage,
        setPage,
        pageInfo,
        reportSort,
        setReportSort,
        setReportColumnFilter,
        reportDateRange,
        setReportDateRange,
        exportCompletedReports,
        isExporting,
        approveItem,
        approveItems,
        denyItem,
        denyItems,
        pendingItem,
        updateExpenseCategory,
        approveReport,
        denyReport,
        reportExpensesMap,
        loadReportExpenses,
        allCategories,
        activeCategories,
        categoriesStatus,
        reloadCategories: loadAllCategories,
        getLocationPredictions,
        getReportComments,
        createReportComment,
        getExpenseComments,
        getPolicyViolationComments,
        createExpenseComment,
        getExpenseHistory,
        locateReport,
    };
}
